import { ListIcon } from "lucide-react"

import type { Order, SubOrder } from "@/api/api-client"
import { FulfillmentType } from "@/api/api-client"
import { FulfillmentDetails } from "@/components/orders/FulfillmentDetails"
import { OrderItemsTable } from "@/components/orders/OrderItemsTable"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { WarehouseDisplay } from "@/components/warehouse/WarehouseDisplay"

import type { VendorOrderLabels } from "./vendor-order-types"

interface VendorOrderItemsAccordionProps {
  subOrder: SubOrder
  masterOrder: Order
  labels: Pick<
    VendorOrderLabels,
    "skuBreakdown" | "product" | "sku" | "quantity" | "unitQuote" | "totalAmount" | "address"
  >
  isWarehouseExpanded: boolean
  onToggleWarehouse: () => void
}

export function VendorOrderItemsAccordion({
  subOrder,
  masterOrder,
  labels,
  isWarehouseExpanded,
  onToggleWarehouse,
}: VendorOrderItemsAccordionProps) {
  const isPickup = subOrder.fulfillmentType === FulfillmentType.Pickup

  return (
    <Accordion type="single" collapsible className="w-full border-t border-border bg-muted/10">
      <AccordionItem value="skus" className="border-none">
        <AccordionTrigger className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:no-underline">
          <span className="flex items-center gap-2">
            <ListIcon className="h-4 w-4" />
            {labels.skuBreakdown?.replace("{count}", (subOrder.items?.length || 0).toString())}
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-6 pt-0">
          <div className="mt-2 overflow-hidden rounded-lg border border-border/50 bg-background">
            <OrderItemsTable
              items={subOrder.items}
              labels={{
                product: labels.product,
                sku: labels.sku,
                quantity: labels.quantity,
                unitPrice: labels.unitQuote,
                total: labels.totalAmount,
                notAvailable: "N/A",
              }}
            />
          </div>

          <div className="mt-4 flex items-start gap-4 rounded-lg border border-border/50 bg-muted/20 p-4">
            {isPickup ? (
              <WarehouseDisplay
                warehouse={subOrder.pickupWarehouse}
                fallbackName={`Almacén #${subOrder.pickupWarehouseId}`}
                isExpanded={isWarehouseExpanded}
                onToggle={onToggleWarehouse}
                className="w-full"
              />
            ) : (
              <FulfillmentDetails
                compact
                title={labels.address || "SHIPPING ADDRESS"}
                recipientName={masterOrder.buyer?.displayName || masterOrder.receiverName}
                phone={masterOrder.phone}
                street={masterOrder.street}
                externalNumber={masterOrder.externalNumber}
                city={masterOrder.city}
                state={masterOrder.state}
                className="w-full"
              />
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
